import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'

import { ApiError } from '@/lib/api'
import { deskSsoApi } from '@/lib/api/desk-sso-api'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { m } from '@/lib/paraglide/messages'
import { getAnalyticsActor, posthog } from '@/lib/posthog'

export const Route = createFileRoute('/_app/_authed/support/t/$ref')({
    component: DeskSsoRedirectPage,
})

type DeskSsoHandoffOutcome =
    'redirected' | 'forbidden' | 'unavailable' | 'failed'

/**
 * One event per hand-off attempt. Answers: do staff actually use the SSO
 * hand-off, and how often does it fail (forbidden / not configured / error)
 * instead of redirecting? No ticket ref, URL or raw error text — those are
 * exactly what a Zalo hand-off link carries, so they never leave the browser.
 */
function captureDeskSsoHandoff(outcome: DeskSsoHandoffOutcome) {
    try {
        const actor = getAnalyticsActor()
        if (!actor) return
        posthog.capture('desk_sso_handoff_attempted', {
            schema_version: 1,
            event_id: crypto.randomUUID(),
            ...actor,
            route_template: '/support/t/$ref',
            outcome,
        })
    } catch {
        // Analytics must never block the redirect or its error screen.
    }
}

function classifyDeskSsoFailure(error: unknown): DeskSsoHandoffOutcome {
    if (error instanceof ApiError) {
        if (error.code === 'FORBIDDEN') return 'forbidden'
        if (error.code === 'DESK_SSO_DISABLED') return 'unavailable'
    }
    return 'failed'
}

export function DeskSsoRedirectPage() {
    const { ref } = Route.useParams()

    const { isError, error } = useQuery({
        queryKey: ['desk-sso-link', ref],
        queryFn: async () => {
            let res: Awaited<ReturnType<typeof deskSsoApi.getLink>>
            try {
                res = await deskSsoApi.getLink(ref)
            } catch (err) {
                captureDeskSsoHandoff(classifyDeskSsoFailure(err))
                throw err
            }
            if (!res.data) {
                captureDeskSsoHandoff('failed')
                throw new Error('desk-sso response missing url')
            }
            captureDeskSsoHandoff('redirected')
            // Full-page hand-off to the Desk portal — the ERP tab is done
            // with this request, so a hard navigation (not router push) is
            // the correct primitive here.
            window.location.replace(res.data.url)
            return res.data
        },
        retry: false,
        staleTime: Infinity,
    })

    if (isError) {
        // FORBIDDEN is a shared, context-dependent code (owner: single
        // error-i18n map denylists it) — this screen owns its own wording
        // rather than adding a global mapping. Owner decision (27/09): only
        // staff hold support.desk_sso; everyone else sees this message.
        const message =
            (error instanceof ApiError && error.code === 'FORBIDDEN'
                ? m.desk_sso_error_forbidden()
                : error instanceof ApiError &&
                  resolveApiErrorMessage(error.code, error.params)) ||
            m.desk_sso_error_unexpected()
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
                <AlertCircle className="size-12 text-destructive" />
                <h1 className="text-xl font-semibold text-destructive">
                    {m.desk_sso_error_title()}
                </h1>
                <p className="text-muted-foreground max-w-sm">{message}</p>
            </div>
        )
    }

    return (
        <div className="min-h-screen flex items-center justify-center">
            <p className="text-muted-foreground animate-pulse">
                {m.desk_sso_redirecting()}
            </p>
        </div>
    )
}
